import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import Svg from '../shared/Svg';
import colors from '../../utils/colors';
import { addingTime, addTimeRecord, setSelectedTimeRecord, updateTimeRecord } from '../../actions/time';
import { TimeConfigFields } from './TimeConfigFields';
import { TimeDuration } from './TimeDuration';
import { TimeFormNotification } from './TimeFormNotification';
import { StatusBarAlert } from '../statusBar/StatusBarAlert';
import { DisposableAlert } from '../shared/DisposableAlert';
import { changeAlertText, clearAlertText } from '../../actions';
import Save from '../save/Save';
import { savingToTimeline } from '../../actions/mail';
import { checkBillingRules } from '../../../api/emailsApi/time';
import * as RootActions from '../../actions/index';
import TimeBillingErrors from './TimeBillingErrors';

import './TimeFormContainer.scss';

const TimeFormContainer = () => {
  const dispatch = useDispatch();

  const configFields = useSelector((state) => state.time.config.fields);
  const sendOnSave = useSelector((state) => state.time.sendOnSave);
  const forceEmailSave = useSelector((state) => state.time.forceEmailSave);
  const emailSaved = useSelector((state) => state.mail.isEmailSaved);
  const timeTrackingMode = useSelector((state) => state?.mail?.settings?.timeSettings?.timeTrackingMode);
  const showTimeRequired = timeTrackingMode === 'timeRequired' && sendOnSave;
  const timeNamespace = useSelector((state) => state.mail.settings?.override?.timeNamespace || 'litify_time');
  const isLoading = useSelector((state) => state.app.isLoading);
  const alertText = useSelector((state) => state.app.alertText);
  const saveAll = (showTimeRequired || sendOnSave) && !Object.values(emailSaved).includes(false);
  const [inputs, setInputs] = useState({});
  const [validationError, setValidationError] = useState(false);
  const [userFields, setUserFields] = useState(JSON.parse(JSON.stringify(configFields)));
  const saveButtonRef = useRef(null);
  const selectedTimeRecord = useSelector((state) => state.time.selectedTimeRecord);


  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (selectedTimeRecord) {
      // may need to add function to reformat selectedTimeRecords to match the configFields
      // update config so the fields have default set to the selectedTimeRecord values
      const updatedConfig = JSON.parse(JSON.stringify(configFields));
      for (const field of updatedConfig) {
        if (selectedTimeRecord?.[field.name]) {
          field.default = selectedTimeRecord[field.name];
        }
      }
      // remove errorMessage from selectedTimeRecord
      const updatedTimeRecord = { ...selectedTimeRecord };
      updatedTimeRecord.saved = false;
      updatedTimeRecord.fixLater = false;
      delete updatedTimeRecord.errorMessage;
      // remove fields that are not in the config
      for (const field in updatedTimeRecord) {
        if (!updatedConfig.find((configField) => configField.name === field) && field !== 'PK' && field !== 'SK' && field !== 'emailRecordId') {
          delete updatedTimeRecord[field];
        }
      }
      // set inputs to default values
      setInputs(updatedTimeRecord);
      setUserFields(updatedConfig);
      dispatch(changeAlertText(selectedTimeRecord?.errorMessage, 'error'));
    }
  }, [selectedTimeRecord]);

  const handleChange = (event, data) => {
    const name = event?.target?.name || data?.fieldName;
    // we must check for data.value first to handle the case that the picklist is being updated.
    const value = event?.target?.type === 'checkbox' ? event.target.checked : data?.value || event.target.value;
    setInputs((values) => ({ ...values, [name]: value, fixLater: false }));
  };

  const updateRequiredFields = (requiredFields) => {
    setInputs((values) => ({ ...values, ...requiredFields }));
  };

  const onBackClicked = () => {
    if (selectedTimeRecord) {
      dispatch(setSelectedTimeRecord(null));
    }
    setUserFields(JSON.parse(JSON.stringify(configFields)));
    dispatch(addingTime(false));
    dispatch(clearAlertText());
    if (forceEmailSave) {
      dispatch(savingToTimeline(false));
    }
  };

  // handle billing rule errors and warnings later
  const onFixLater = async () => {
    if (sendOnSave) {
      saveButtonRef?.current?.handleSave(true);
    } else {
      await validateAndAddTime();
    }
  };

  const onAddTime = async (fromFixLater) => {
    if (fromFixLater !== true) {
      inputs.fixLater = false;
    }
    await validateAndAddTime();
  };

  // add time record
  const validateAndAddTime = async () => {
    // validate form
    const newTime = await validateForm();
    newTime.timeRecordId = selectedTimeRecord?.timeRecordId;

    // add time record
    try {
      if (selectedTimeRecord) {
        const recordIndex = selectedTimeRecord.recordIndex;
        dispatch(setSelectedTimeRecord(null));
        dispatch(updateTimeRecord(recordIndex, newTime));
      } else {
        dispatch(addTimeRecord(newTime));
      }
      batch(() => {
        dispatch(addingTime(false));
        dispatch(clearAlertText());
      });
    } catch (e) {
      console.error(e);
      dispatch(changeAlertText('Error saving time record', 'error'));
    }
  };

  const onDurationUpdate = (value) => {
    setInputs((values) => ({ ...values, [`${timeNamespace}__lit_Duration__c`]: value }));
    // when and update is made to the duration, set fixLater to false to rerun billing rules
    inputs.fixLater = false;
  };

  // check form for required fields
  const validateForm = async () => {
    let newTime = { ...inputs };
    let vError = false;
    const errorFields = [];
    const fields = [...userFields];
    for (const field of fields) {
      if (field.required &&
        field.fieldType !== 'boolean' &&
        ![`${timeNamespace}__lit_Rate__c`].includes(field.name) &&
        (!inputs[field.name] || inputs[field.name] === '')
      ) {
        field.error = `${field.label} is required`;
        vError = true;
        errorFields.push(field.label);
      } else {
        field.error = '';
      }
    }
    setUserFields(fields);
    if (vError) {
      setValidationError(true);
      throw new Error(`Missing required fields: ${errorFields.join(', ')}`);
    } else {
      setValidationError(false);
    }

    // handle billing rule errors and warnings
    if (!inputs.fixLater) {
      dispatch(RootActions.isLoading(true));
      let response;
      // check billing rules
      try {
        const timeEntry = {
          ...inputs,
        };
        response = saveAll && timeTrackingMode !== 'timeReminder' ? {} : (await checkBillingRules(timeEntry))?.data;
        setInputs((values) => ({ ...values, ...response, fixLater: true }));
      } catch (e) {
        console.error(e);
        if (e.statusCode === 404) {
          return;
        }
        console.error('Error checking billing rules', e);
        dispatch(changeAlertText('Error checking billing rules', 'error'));
      } finally {
        dispatch(RootActions.isLoading(false));
      }
      newTime = { ...inputs, ...response };
      // change alert if errors are found
      if (response?.errorRules?.length > 0) {
        const alertText = 'Entry contains at least one billing rule error. Entries with errors cannot be billed. Fix Later or Update Entry.';
        dispatch(changeAlertText(alertText, 'error'));
        throw new Error(alertText);
      }
    }
    dispatch(RootActions.isLoading(false));
    return newTime;
  };

  const containerPadding =
    alertText && showTimeRequired ? '11rem' :
    alertText ? '7rem' :
    showTimeRequired ? '8rem' :
    '2rem';

  return (
    <div className={ isLoading ? 'time-form-container-deactivated' : 'time-form-container'}>
      <div className='time-form-fixed-header'>
        { alertText &&
        <DisposableAlert
          child={<StatusBarAlert />}
          disposeFunction={() => {
            if (!inputs.errorRules && !inputs.warningRules) dispatch(clearAlertText());
          }}
        />}
        { showTimeRequired && <TimeFormNotification /> }
        {inputs.errorRules && <TimeBillingErrors errors={inputs.errorRules} warnings={inputs.warningRules}/>}
        <div className='time-form-back-container'>
          <button // back button out of line on left side compared to mock
            className='time-form-back'
            onClick={onBackClicked}
          >
            <Svg
              className='time-form-back-icon'
              data='/assets/back.svg'
              fill={colors.strongBlue}
              onClick={onBackClicked}
            />
        Back
          </button>
        </div>
      </div>
      <div className={`time-form-main-section-${containerPadding}`}>
        <div className='time-form-title'>Time</div>
        <div className='time-form-description'>Add Time Entry</div>
        <div className='time-form-config-fields'>
          <TimeConfigFields configFields={userFields} handleChange={handleChange} updateRequiredFields={updateRequiredFields} />
          <TimeDuration onChange={onDurationUpdate} durationValue={selectedTimeRecord && selectedTimeRecord?.[`${timeNamespace}__lit_Duration__c`]} />
        </div>
        <div className='time-form-button-container'>
          { validationError && <div className='time-form-validation-error'>Complete required fields</div> }
          <button className='time-form-button-cancel' onClick={onBackClicked}>Cancel</button>
          { inputs.errorRules?.length > 0 && <button disabled={!inputs.fixLater} className='time-form-button-fix-later' onClick={onFixLater}>Fix Later</button>}
          { sendOnSave ?
          <Save ref={saveButtonRef} timeRecordObject={inputs} saveAll={saveAll} handleClick={onAddTime} /> :
          <button className='time-form-button-submit' onClick={onAddTime}>Add Time</button> }
        </div>
      </div>
    </div>
  );
};

export default TimeFormContainer;
